import { useState } from 'react'
import { Cube, PencilSimple, Plus, Trash } from '@phosphor-icons/react'
import { Banner, DataTable, EmptyState, Field, Input, Select, type Column } from '@/ui'
import { useCan } from '@/auth/context'
import { errorMessage } from '../api/core'
import { useDeleteSku, useSaveSku, useSkuList, type SkuRow } from '../api/masterData'
import { useCrops } from '../api/reference'
import { ActionButton } from '../components/Can'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { FilterBar, SearchFilter, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { Code, RefLink } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import { serverProblem } from '../model/serverError'
import {
  createSkuBody,
  displayGtin,
  draftFromSku,
  emptySkuDraft,
  normaliseSkuCode,
  skuOk,
  updateSkuBody,
  validateSkuDraft,
  type SkuDraft,
} from '../model/skuForm'

const LIMIT = 100

type Editing = { mode: 'create' } | { mode: 'edit'; row: SkuRow }

/**
 * Products (SKUs, F7.1-a): what is packed — crop, net weight per pouch, GTIN, shelf life, carton.
 * A product needs an approved label version at the hall (Label versions) before it can be packed.
 */
export function SkusPage() {
  const [f, setF, { reset, active }] = useUrlState({ cropId: '', search: '', page: 1 })
  const q = useSkuList({ page: f.page, limit: LIMIT, cropId: f.cropId || undefined, search: f.search || undefined })
  const crops = useCrops()
  const can = useCan()
  const [editing, setEditing] = useState<Editing | null>(null)
  const [removing, setRemoving] = useState<SkuRow | null>(null)
  const rows = q.data?.rows ?? []

  const columns: Column<SkuRow>[] = [
    { id: 'code', header: 'Code', cell: (r) => <Code>{r.code}</Code>, sortValue: (r) => r.code },
    { id: 'name', header: 'Product', cell: (r) => <span className="font-semibold">{r.name}</span>, sortValue: (r) => r.name },
    { id: 'crop', header: 'Crop', cell: (r) => r.cropName ?? '—', hideBelow: 'sm' },
    { id: 'net', header: 'Net per pouch', align: 'right', mono: true, cell: (r) => `${r.netG.toLocaleString('en-IN')} g` },
    {
      id: 'gtin',
      header: 'GTIN',
      hideBelow: 'md',
      cell: (r) => (r.gtin ? <span className="font-mono">{displayGtin(r.gtin)}</span> : <span className="text-muted">None yet</span>),
    },
    {
      id: 'shelf',
      header: 'Shelf life',
      hideBelow: 'md',
      align: 'right',
      cell: (r) => (r.shelfLifeDays ? `${r.shelfLifeDays} days` : <span className="text-muted">—</span>),
    },
    {
      id: 'carton',
      header: 'Pouches / carton',
      hideBelow: 'lg',
      align: 'right',
      cell: (r) => r.pouchesPerCarton ?? <span className="text-muted">—</span>,
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <ActionButton perm="skus:update" hide size="sm" variant="ghost" leadingIcon={<PencilSimple />} onClick={() => setEditing({ mode: 'edit', row: r })}>
            Edit
          </ActionButton>
          <ActionButton perm="skus:delete" hide size="sm" variant="ghost" leadingIcon={<Trash />} onClick={() => setRemoving(r)}>
            Delete
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Setup"
      title="Products"
      description={
        <>
          The packs we sell: crop, net weight per pouch, barcode (GTIN), shelf life and carton size. Before a product can be packed at a hall it needs an
          approved label version — see <RefLink to="/c/packing/labels">Label versions</RefLink>.
        </>
      }
      actions={
        <ActionButton perm="skus:create" leadingIcon={<Plus />} onClick={() => setEditing({ mode: 'create' })}>
          Add product
        </ActionButton>
      }
    >
      <DataTable<SkuRow>
        caption="Products"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={can('skus:update') ? (r) => setEditing({ mode: 'edit', row: r }) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        empty={
          <EmptyState
            icon={<Cube />}
            title={active ? 'No products match' : 'No products yet'}
            description="Add one product per pack size, e.g. Groundnut kernel 500 g."
            compact
          />
        }
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Search" value={f.search} onChange={(search) => setF({ search, page: 1 })} placeholder="Code or name" />
            <SelectFilter
              label="Crop"
              value={f.cropId}
              onChange={(cropId) => setF({ cropId, page: 1 })}
              options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </FilterBar>
        }
      />
      {editing && <SkuDialog editing={editing} crops={crops.data ?? []} onClose={() => setEditing(null)} />}
      <ConfirmDelete row={removing} onClose={() => setRemoving(null)} />
    </ConsolePage>
  )
}

function SkuDialog({
  editing,
  crops,
  onClose,
}: {
  editing: Editing
  crops: ReadonlyArray<{ id: string; name: string }>
  onClose: () => void
}) {
  const edit = editing.mode === 'edit' ? editing.row : null
  const [d, setD] = useState<SkuDraft>(edit ? draftFromSku(edit) : emptySkuDraft())
  const [touched, setTouched] = useState<Set<keyof SkuDraft>>(new Set())
  const save = useSaveSku({ inline: true })
  const p = validateSkuDraft(d, editing.mode)
  const touch = (k: keyof SkuDraft) => setTouched((s) => new Set(s).add(k))
  const shown = (k: keyof SkuDraft) => (touched.has(k) ? p[k] : undefined)
  const err = save.isError ? serverProblem(save.error) : null
  const num = (k: keyof SkuDraft) => (e: { target: { value: string } }) => setD({ ...d, [k]: e.target.value.replace(/[^\d.]/g, '') })

  return (
    <FormDialog
      open
      onClose={onClose}
      title={edit ? `Edit ${edit.code}` : 'Add product'}
      saveLabel={edit ? 'Save' : 'Add product'}
      loading={save.isPending}
      canSave={skuOk(p)}
      onSave={() => save.mutateAsync(edit ? { id: edit.id, body: updateSkuBody(d) } : { body: createSkuBody(d) })}
    >
      {err && (
        <Banner tone="error" title={err.status === 409 ? 'Already exists' : err.message}>
          {err.status === 409 ? `${err.message}. Use another code or GTIN.` : err.details.join(' · ')}
        </Banner>
      )}
      {edit ? (
        <p className="text-[14px] text-muted">
          Code <Code>{edit.code}</Code>, crop ({edit.cropName ?? '—'}) and net weight ({edit.netG.toLocaleString('en-IN')} g) cannot change: pack lots and
          labels carry them. For another pack size, add a new product.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Code" required error={shown('code')} hint="e.g. GN-K-500">
            <Input
              value={d.code}
              onChange={(e) => setD({ ...d, code: normaliseSkuCode(e.target.value) })}
              onBlur={() => touch('code')}
              maxLength={20}
              placeholder="GN-K-500"
              className="font-mono"
              data-autofocus
            />
          </Field>
          <Field label="Crop" required error={shown('cropId')}>
            <Select
              value={d.cropId}
              onChange={(cropId) => {
                setD({ ...d, cropId })
                touch('cropId')
              }}
              onBlur={() => touch('cropId')}
              placeholder="Choose…"
              options={crops.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
          <Field label="Net weight per pouch" required error={shown('netG')}>
            <Input inputMode="numeric" value={d.netG} onChange={num('netG')} onBlur={() => touch('netG')} trailing="g" placeholder="500" className="font-mono" />
          </Field>
        </div>
      )}
      <Field label="Product name" required error={shown('name')} hint="As printed on the pouch">
        <Input
          value={d.name}
          onChange={(e) => setD({ ...d, name: e.target.value })}
          onBlur={() => touch('name')}
          maxLength={120}
          placeholder="Groundnut kernel 500 g"
          data-autofocus={edit ? true : undefined}
        />
      </Field>
      <Field
        label="GTIN (barcode number)"
        error={d.gtin ? (touched.has('gtin') ? p.gtin : undefined) : undefined}
        hint={edit?.gtin ? 'Empty it to remove the GTIN.' : 'Optional until GS1 membership. 8, 12, 13 or 14 digits.'}
      >
        <Input
          inputMode="numeric"
          value={d.gtin}
          onChange={(e) => setD({ ...d, gtin: e.target.value.replace(/\D/g, '').slice(0, 14) })}
          onBlur={() => touch('gtin')}
          placeholder="8901234567897"
          className="font-mono"
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Shelf life" error={shown('shelfLifeDays')} hint="Best before = packed + this">
          <Input inputMode="numeric" value={d.shelfLifeDays} onChange={num('shelfLifeDays')} onBlur={() => touch('shelfLifeDays')} trailing="days" placeholder="180" className="font-mono" />
        </Field>
        <Field label="Allowed shortfall" error={shown('toleranceG')} hint="Per pouch, under the net weight">
          <Input inputMode="decimal" value={d.toleranceG} onChange={num('toleranceG')} onBlur={() => touch('toleranceG')} trailing="g" placeholder="15" className="font-mono" />
        </Field>
        <Field label="Pouches per carton" error={shown('pouchesPerCarton')}>
          <Input inputMode="numeric" value={d.pouchesPerCarton} onChange={num('pouchesPerCarton')} onBlur={() => touch('pouchesPerCarton')} placeholder="24" className="font-mono" />
        </Field>
      </div>
      {edit && (
        <p className="text-[13px] text-muted">Shelf life, shortfall and carton size can be changed but not removed once set.</p>
      )}
    </FormDialog>
  )
}

function ConfirmDelete({ row, onClose }: { row: SkuRow | null; onClose: () => void }) {
  const del = useDeleteSku()
  return (
    <ConfirmDialog
      open={Boolean(row)}
      onClose={onClose}
      title={`Delete ${row?.code ?? ''}?`}
      description="Only a product that was never packed can be deleted."
      confirmLabel="Delete product"
      tone="danger"
      loading={del.isPending}
      onConfirm={async () => {
        if (!row) return
        await del.mutateAsync(row.id)
        onClose()
      }}
    />
  )
}
